import React, { useState, useRef, useLayoutEffect } from 'react';

/**
 * LimelightNav — A text-based navigation bar with a "limelight" spotlight effect.
 * Black & White theme variant.
 */

export const LimelightNav = ({
    items = [],
    defaultActiveIndex = 0,
    onTabChange,
    className = '',
}) => {
    const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
    const [isReady, setIsReady] = useState(false);
    const navItemRefs = useRef([]);
    const limelightRef = useRef(null);

    useLayoutEffect(() => {
        if (items.length === 0) return;

        const limelight = limelightRef.current;
        const activeItem = navItemRefs.current[activeIndex];

        if (limelight && activeItem) {
            const newLeft = activeItem.offsetLeft + activeItem.offsetWidth / 2 - limelight.offsetWidth / 2;
            limelight.style.left = `${newLeft}px`;

            if (!isReady) {
                setTimeout(() => setIsReady(true), 50);
            }
        }
    }, [activeIndex, isReady, items]);

    if (items.length === 0) return null;

    const handleItemClick = (index, itemOnClick) => {
        setActiveIndex(index);
        onTabChange?.(index);
        itemOnClick?.();
    };

    return (
        <nav className={`relative inline-flex items-center h-14 rounded-xl bg-white/80 backdrop-blur-md border border-gray-200/50 px-1 shadow-lg shadow-black/5 ${className}`}>
            {items.map(({ id, label, onClick }, index) => (
                <a
                    key={id}
                    ref={el => (navItemRefs.current[index] = el)}
                    className={`relative z-20 flex h-full cursor-pointer items-center justify-center px-5 py-2 text-sm font-bold uppercase tracking-wider transition-all duration-200 ${activeIndex === index
                            ? 'text-black scale-105'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                    onClick={() => handleItemClick(index, onClick)}
                >
                    {label}
                </a>
            ))}

            {/* Limelight spotlight effect — black theme */}
            <div
                ref={limelightRef}
                className={`absolute top-0 z-10 w-11 h-[4px] rounded-full bg-black shadow-[0_40px_12px_rgba(0,0,0,0.3)] ${isReady ? 'transition-[left] duration-400 ease-in-out' : ''
                    }`}
                style={{ left: '-999px' }}
            >
                <div className="absolute left-[-30%] top-[4px] w-[160%] h-12 [clip-path:polygon(5%_100%,25%_0,75%_0,95%_100%)] bg-gradient-to-b from-black/15 to-transparent pointer-events-none" />
            </div>
        </nav>
    );
};
